import {FiSave, FiTrash2} from "react-icons/fi";

import {Button} from "@/components/button/Button.tsx";
import {Icon} from "@/components/icon/Icon.tsx";
import {Select} from "@/components/select/Select.tsx";
import {SelectOption} from "@/components/select/Select.types.ts";
import {MetricsExplorerView} from "@/lib/types/metricsExplorer.ts";
import {ExplorerConfig} from "@/pages/metricsExplorer/explorerConfig.ts";

import styles from "./SavedViewsBar.module.scss";

interface SavedViewsBarProps {
  views: MetricsExplorerView[];
  activeView: MetricsExplorerView | null;
  canSave: boolean;
  onOpen: (view: MetricsExplorerView) => void;
  onSave: () => void;
  onSaveAs: () => void;
  onDelete: () => void;
  configOf: (view: MetricsExplorerView) => ExplorerConfig | null;
}

const NEW_CHART = "";

export const SavedViewsBar = ({
  views,
  activeView,
  canSave,
  onOpen,
  onSave,
  onSaveAs,
  onDelete,
  configOf,
}: SavedViewsBarProps) => {
  // A chart saved before a config change may no longer parse. Marking it beats opening a
  // blank explorer and leaving the user to work out why.
  const options: SelectOption[] = [
    {value: NEW_CHART, label: "Unsaved chart"},
    ...views.map((view) => ({
      value: String(view.id),
      label: configOf(view) ? view.name : `${view.name} (unreadable)`,
    })),
  ];
  const selected = options.find((option) => option.value === String(activeView?.id ?? NEW_CHART));

  return (
    <div className={styles.bar}>
      <div className={styles.picker}>
        <span className={styles.label}>Saved charts</span>
        <Select
          size="small"
          options={options}
          value={selected}
          onChange={(option) => {
            const value = (option as SelectOption | null)?.value ?? NEW_CHART;
            const view = views.find((item) => String(item.id) === value);
            if (view) {
              onOpen(view);
            }
          }}
        />
      </div>

      <div className={styles.actions}>
        <Button variant="secondary" size="small" onClick={onSave} disabled={!canSave}>
          <Icon icon={FiSave} /> {activeView ? "Save" : "Save chart"}
        </Button>
        {activeView && (
          <>
            <Button variant="secondary" size="small" onClick={onSaveAs} disabled={!canSave}>
              Save as new
            </Button>
            <Button variant="secondary" size="small" onClick={onDelete}>
              <Icon icon={FiTrash2} /> Delete
            </Button>
          </>
        )}
      </div>
    </div>
  );
};
